import { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
    Backdrop,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogContentText,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone';
import { deleteAsset } from 'api/DigitalAssetApi';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import './AdminDigitalAssets.scss';
import ReduxStore from '../../ReduxStore';
import { getIn } from '../../lib/immutableObject';

const AdminDigitalAssets = ({ assets, setAssets }) => {
  const [displayedImages, setDisplayedImages] = useState({});
  const [deleting, setDeleting] = useState(false);
  const [open, setOpen] = useState(false);
  const [assetUrl, setAssetUrl] = useState(null);

  const showImage = (type, url) => () => {
    if (type === 'jpg' || type === 'png') {
      setDisplayedImages(prev => ({ ...prev, [url]: true }));
    }
  };

  const rpDataUrl = getIn(ReduxStore.getState(), ['settings', 'rpd', 'baseUrl']);

  const onDelete = () => {
      setDeleting(true);
      setOpen(false);
      deleteAsset(assetUrl)
          .then(() => {
              setAssets(JSON.parse(JSON.stringify(assets)).filter(asset => asset.url !== assetUrl));
          })
          .finally(() => {
              setDeleting(false);
          });
  };

  const showDeleteDialog = (url) => {
      setAssetUrl(url);
      setOpen(true);
  };

  const handleClose = () => setOpen(false);

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell>Property Id</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Username</TableCell>
            <TableCell>Franchise Id</TableCell>
            <TableCell>Franchise Name</TableCell>
            <TableCell>Resource</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Country</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {assets.map(({ url, propertyId, source, username, franchiseId, franchiseName, created, country }) => {
            const type = url.replace(/[^?]+\.(\w+)(\?.*)?$/, '$1');

            return (
                <TableRow key={propertyId}>
                  <TableCell>
                    <a
                        href={`${encodeURI(rpDataUrl)}/property/detail.html?propertyId=${encodeURIComponent(propertyId)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                      {propertyId}
                    </a>
                  </TableCell>
                  <TableCell>{source}</TableCell>
                  <TableCell>{username}</TableCell>
                  <TableCell>{franchiseId}</TableCell>
                  <TableCell>{franchiseName}</TableCell>
                  <TableCell>
                    {displayedImages[url] ? (
                        <a href={encodeURI(url)} target="_blank" rel="noopener noreferrer">
                          <img src={encodeURI(url)} alt={type} />
                        </a>
                    ) : (
                        <a onMouseEnter={showImage(type, url)} href={encodeURI(url)} target="_blank" rel="noopener noreferrer">
                          {type}
                        </a>
                    )}
                  </TableCell>
                  <TableCell>{moment(created).format('YYYY-MM-DD hh:mm A')}</TableCell>
                  <TableCell>{country}</TableCell>
                  <TableCell align={'center'}>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<DeleteForeverTwoToneIcon />}
                        onClick={() => showDeleteDialog(url)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Backdrop open={deleting} style={{ color: '#fff', zIndex: 2 }}>
          <CircularProgress color="inherit" />
      </Backdrop>
      <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
              <DialogContentText>
                  Are you sure you want to delete this record? This action cannot be undone.
                  <br /> {assetUrl}
              </DialogContentText>
          </DialogContent>
          <DialogActions>
              <Button onClick={handleClose} color="primary">Cancel</Button>
              <Button onClick={onDelete} color="error" variant="contained">Delete</Button>
          </DialogActions>
      </Dialog>
    </TableContainer>
  );
};

AdminDigitalAssets.propTypes = {
  assets: PropTypes.arrayOf(
      PropTypes.shape({
        propertyId: PropTypes.number.isRequired,
        source: PropTypes.string.isRequired,
        username: PropTypes.string.isRequired,
        franchiseId: PropTypes.string.isRequired,
        franchiseName: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }).isRequired
  ).isRequired,
  setAssets: PropTypes.func.isRequired,
};

export default AdminDigitalAssets;